/**
 * @prettier
 * @author Nic Ebner
 * @author Alex Clary
 */
import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'

import LgyTooltip from '../lgy-tooltip/LgyTooltip'
import { uploadFile, fetchChunkedUploadProperties } from 'app/actions/chunkedUploadActions'
import { uploadFileTypeIsValid } from 'app/utils/fieldUtils'

const LgyChunkedFileUpload = ({
  id,
  className,
  name,
  label,
  tooltip,
  error,
  uploadFile,
  fileId,
  inIframe,
  uploadComplete,
  chunkError,
  initError,
  properties,
  fetchChunkedUploadProperties
}) => {
  let [file, setFile] = useState()

  useEffect(() => {
    if (!properties) {
      console.log('Do not have file upload properties')
      fetchChunkedUploadProperties()
    }
  })

  useEffect(() => {
    if (inIframe && fileId !== undefined) {
      window.parent.postMessage(
        JSON.stringify({ fileId: fileId, uploadComplete: uploadComplete }),
        window.location.origin
      )
    }
  }, [inIframe, fileId, uploadComplete])

  useEffect(() => {
    if (inIframe && (chunkError || initError)) {
      window.parent.postMessage(JSON.stringify({ error: initError ? initError : chunkError }), window.location.origin)
    }
  }, [inIframe, chunkError, initError])

  const handleChange = file => {
    window.parent.postMessage(
      JSON.stringify({
        fileChanged: true
      }),
      window.location.origin
    )

    if (!file) {
      console.log('File is falsy.')
      return false
    }
    console.info(`Filename: "${file.name}" File size: ${file.size} bytes`)
    const fileSizeIsValid = properties && file.size <= properties.maximumFileSize && file.size > 0
    const filenameIsValid = uploadFileTypeIsValid(file.name)

    if (!fileSizeIsValid) {
      window.parent.postMessage(
        JSON.stringify({
          validationError: `File (${file.size} bytes) is empty or larger than max allowed (${properties.maximumFileSize} bytes)`
        }),
        window.location.origin
      )
    }

    if (!filenameIsValid) {
      window.parent.postMessage(
        JSON.stringify({
          validationError: `Filename "${file.name}" is not valid. pdf, doc, docx, xlsx, xls, tif, tiff, gif, jpg, jpeg, png file extensions are supported for upload.`
        }),
        window.location.origin
      )
    }

    if (fileSizeIsValid && filenameIsValid) {
      // Tell WebLGY to clear the error message
      window.parent.postMessage(JSON.stringify({ validationError: false }), window.location.origin)
      setFile(file)

      if (inIframe) {
        const fileInfo = JSON.stringify({ fileName: file.name, fileSize: file.size })
        window.parent.postMessage(fileInfo, window.location.origin)
      }
    } else {
      return false
    }
  }

  const handleSubmit = event => {
    event.preventDefault()
    window.parent.postMessage(
      JSON.stringify({
        uploadStarted: true
      }),
      window.location.origin
    )
    uploadFile(file, properties?.preferredChunkSize)
  }

  return properties ? (
    <form id='chunkedUploadForm' onSubmit={handleSubmit}>
      <div className={error ? 'usa-input-error lgy-input-field' : 'lgy-input-field'}>
        <label htmlFor={id}>
          {label}
          {tooltip && <LgyTooltip message={tooltip} id={`${id}-tip`} />}
        </label>
        {error && (
          <span className='usa-input-error-message' id={`${id}-error`} role='alert'>
            {error}
          </span>
        )}
        <input
          id={id}
          type='file'
          name={name}
          className={file ? className + ' usa-input-success' : className}
          onChange={e => handleChange(e.target.files[0])}
          required
          accept='.pdf, .doc, .docx, .xlsx, .xls, .tif, .tiff, .gif, .jpg, .jpeg, .png'
          aria-required={true}
          aria-describedby={error ? `${id}-error` : tooltip ? `${id}-tip` : ''}
          aria-invalid={!!error}
        />
      </div>
    </form>
  ) : (
    <span>Loading</span>
  )
}

const mapStateToProps = state => ({
  fileId: state.chunkedUploads.initializeChunkedUploads?.fileId,
  uploadComplete: state.chunkedUploads.uploadChunk?.uploadComplete,
  chunkError: state.chunkedUploads.uploadChunk?.error,
  initError: state.chunkedUploads.initializeChunkedUploads?.error,
  properties: state.chunkedUploads.properties
})

const mapDispatchToProps = { uploadFile, fetchChunkedUploadProperties }

export default connect(mapStateToProps, mapDispatchToProps)(LgyChunkedFileUpload)
